const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

const MAX_RETRIES = 2
const RETRY_DELAY_MS = 1000

// Callback para limpiar sesión cuando hay errores de autenticación
let onAuthError = null
// Callback para mostrar mensaje cuando falla la red (fetch lanza o sin conexión)
let onNetworkError = null

/**
 * Establece el callback que se ejecutará cuando haya errores de autenticación (401/403)
 */
export function setAuthErrorHandler(handler) {
  onAuthError = handler
}

/**
 * Establece el callback que se ejecutará cuando haya error de red (fetch falla)
 */
export function setNetworkErrorHandler(handler) {
  onNetworkError = handler
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Realiza una petición fetch con configuración CORS y credenciales
 * Las peticiones van directamente al backend (localhost:8000)
 * Intercepta errores 401/403 para limpiar la sesión local
 * Reintenta hasta MAX_RETRIES veces solo para métodos GET/HEAD en caso de fallo de red o 5xx/429
 */
export async function apiFetch(url, options = {}) {
  const isFormData = options.body instanceof FormData
  const method = (options.method || 'GET').toUpperCase()
  const isIdempotent = method === 'GET' || method === 'HEAD'

  const defaultOptions = {
    credentials: 'include',
    headers: {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      'Accept': 'application/json',
      ...options.headers,
    },
  }

  const finalOptions = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...options.headers,
    },
  }

  if (options.signal != null) {
    finalOptions.signal = options.signal
  }

  if (isFormData && finalOptions.headers['Content-Type']) {
    delete finalOptions.headers['Content-Type']
  }

  const finalUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`

  let lastError = null
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      const response = await fetch(finalUrl, finalOptions)

      if (response.status === 401 || response.status === 403) {
        if (onAuthError) onAuthError()
      }

      const shouldRetry =
        isIdempotent &&
        attempt < MAX_RETRIES &&
        (response.status >= 500 || response.status === 429)
      if (shouldRetry) {
        lastError = response
        await sleep(RETRY_DELAY_MS * (attempt + 1))
        continue
      }

      return response
    } catch (e) {
      if (e?.name === 'AbortError') throw e
      lastError = e
      if (!isIdempotent || attempt >= MAX_RETRIES) {
        if (onNetworkError) onNetworkError()
        throw e
      }
      await sleep(RETRY_DELAY_MS * (attempt + 1))
    }
  }

  if (onNetworkError && lastError) onNetworkError()
  throw lastError
}

export { API_BASE_URL }

